import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Check, Clapperboard, Scan, Sparkles, X } from "lucide-react";
import { createShapeId, type TLImageShape, type TLShapeId } from "@tldraw/tlschema";
import { useValue } from "@tldraw/state-react";
import type { WorkspaceModel } from "./useWorkspace";
import { CreativeAreaGuide } from "./CreativeAreaGuide";
import {
  creativePlacement,
  creativePreviewSource,
  creativeSourceKey,
  creativeThumbSource,
  imagePrompts,
  isCreativeImage,
  motionPrompts,
  newCreativeDraft,
  withPromptStarter,
  type CreativeDraft,
  type CreativeMode,
} from "./creative-tools";
import "./creative-tools.css";
import { useImageGeneration } from "./useImageGeneration";
import { useVideoGeneration } from "./useVideoGeneration";
import { VideoGenerationControls } from "./VideoGenerationControls";
import { ImageGenerationResult } from "./ImageGenerationResult";
import { ImageGenerationActions } from "./ImageGenerationActions";
import { IMAGE_GENERATION_ENABLED, VIDEO_GENERATION_ENABLED } from "../shared/features";

export type CreativeRequest = { shapeId: TLShapeId; mode: CreativeMode };

export function CreativeTools({
  model,
  request,
  close,
  changeMode,
}: {
  model: WorkspaceModel;
  request: CreativeRequest | null;
  close: () => void;
  changeMode: (mode: CreativeMode) => void;
}) {
  const { editor } = model;
  // Deliberately not board records or browser storage: private, current-session drafts only.
  const [drafts, setDrafts] = useState<Record<string, CreativeDraft>>({});
  const source = useValue(
    "creative source",
    () => {
      if (!request || editor.getIsReadonly()) return null;
      const shape = editor.getShape(request.shapeId);
      const asset =
        shape?.type === "image" && shape.props.assetId
          ? editor.getAsset(shape.props.assetId)
          : undefined;
      if (
        !isCreativeImage(shape, asset, model.room.id) ||
        asset?.type !== "image" ||
        editor.isShapeOrAncestorLocked(shape)
      )
        return null;
      if (editor.getOnlySelectedShapeId() !== shape.id) return null;
      const bounds = editor.getShapePageBounds(shape);
      if (!bounds) return null;
      const view = editor.getViewportScreenBounds();
      const bottom = editor.pageToScreen({
        x: bounds.center.x,
        y: bounds.maxY,
      });
      const right = editor.pageToScreen({ x: bounds.maxX, y: bounds.center.y });
      return {
        shape,
        asset,
        view,
        visible: bounds.collides(editor.getViewportPageBounds()),
        anchor: {
          x: bottom.x - view.x,
          bottom: bottom.y - view.y,
          right: right.x - view.x,
        },
      };
    },
    [editor, request, model.room.id],
  );
  const choices = useValue(
    "creative reference choices",
    () =>
      request
        ? editor.getCurrentPageShapes().flatMap((shape) => {
            const asset =
              shape.type === "image" && shape.props.assetId
                ? editor.getAsset(shape.props.assetId)
                : undefined;
            return shape.id !== request.shapeId &&
              isCreativeImage(shape, asset, model.room.id) &&
              asset?.type === "image"
              ? [
                  {
                    id: shape.id,
                    name: String(
                      shape.meta.name || asset.props.name || "Image",
                    ),
                    thumb: creativeThumbSource(asset),
                  },
                ]
              : [];
          })
        : [],
    [editor, request, model.room.id],
  );
  const [height, setHeight] = useState(360);
  const root = useRef<HTMLElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const prompt = useRef<HTMLTextAreaElement>(null);
  const [guiding, setGuiding] = useState(false);
  const key = source ? creativeSourceKey(source.shape) : "";
  const mode = VIDEO_GENERATION_ENABLED ? request?.mode || "image" : "image";
  const generation = useImageGeneration(model, !!request && mode === "image");
  const video = useVideoGeneration(model, !!request && mode === "video");
  const imageJob = generation.status.jobs.find(
    (job) => job.sourceId === source?.shape.id,
  );
  const imagePlaced = useValue(
    "edited image placement",
    () => !!imageJob && !!editor.getShape(createShapeId(`ai-${imageJob.id}`)),
    [editor, imageJob?.id],
  );
  const draft = drafts[key] || newCreativeDraft();
  const update = (patch: Partial<CreativeDraft>) =>
    setDrafts((current) => ({
      ...current,
      [key]: { ...(current[key] || newCreativeDraft()), ...patch },
    }));

  useEffect(() => {
    setGuiding(false);
    prompt.current?.focus({ preventScroll: true });
  }, [key, mode]);
  useEffect(() => {
    if (request && (!source || !model.canEdit || model.modal || model.cropping))
      close();
  }, [request, source, model.canEdit, model.modal, model.cropping, close]);
  useLayoutEffect(() => {
    const node = content.current;
    if (!node) return;
    // Observe unconstrained content, so collapsing controls also shrinks and
    // repositions a composer whose outer surface previously needed scrolling.
    const measure = () => setHeight(node.scrollHeight + 30);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [key, mode, guiding, draft.references.length, source?.visible]);

  if (
    !request ||
    !source ||
    !model.canEdit ||
    model.modal ||
    model.cropping ||
    !source.visible
  )
    return null;
  const { shape, asset, view, anchor } = source;
  const { docked, ...position } = creativePlacement(view, anchor, height);
  const previewSource = creativePreviewSource(asset);
  const sourceName = String(shape.meta.name || asset.props.name || "Image");
  const generating =
    generation.sending ||
    generation.status.jobs.some((job) => job.state === "running");
  const availableIds = new Set(choices.map((c) => c.id));
  const selectedRefs = draft.references.filter((id) =>
    availableIds.has(id as TLShapeId),
  );
  const staleInputs =
    selectedRefs.length !== draft.references.length ||
    (!!draft.endImage && !availableIds.has(draft.endImage as TLShapeId));

  return (
    <section
      ref={root}
      className="creative-composer floating-surface"
      style={position}
      aria-label="AI creative tools"
      data-docked={docked}
      onPointerDown={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Escape") {
          e.preventDefault();
          close();
          editor.focus();
        }
      }}
    >
      <div ref={content}>
        <header className="creative-heading">
          <strong title={sourceName}>
            {mode === "image" ? "Edit with AI" : "Animate image"}
          </strong>
          <button
            type="button"
            aria-label="Close creative tools"
            onClick={close}
          >
            <X size={17} />
          </button>
        </header>
        {VIDEO_GENERATION_ENABLED && (
          <div className="creative-mode" aria-label="Creative action">
            <button
              type="button"
              aria-pressed={mode === "image"}
              onClick={() => changeMode("image")}
            >
              <Sparkles size={15} /> Image
            </button>
            <button
              type="button"
              aria-pressed={mode === "video"}
              onClick={() => changeMode("video")}
            >
              <Clapperboard size={15} /> Video
            </button>
            <span>
              {mode === "image" ? "Nano Banana Pro" : "Seedance 2.5 · Higgsfield"}
            </span>
          </div>
        )}
        {mode === "image" && generation.status.configured && imageJob && (
          // The latest result leads, so "Add to board" is never below the fold.
          // While generating it is only a progress line; the button says the rest.
          <ImageGenerationResult
            key={imageJob.id}
            job={imageJob}
            add={generation.add}
            placed={imagePlaced}
          />
        )}
        <label className="creative-prompt">
          <span className="creative-sr-status">
            {mode === "image"
              ? "Describe your changes"
              : "Describe the movement"}
          </span>
          <textarea
            ref={prompt}
            maxLength={4000}
            rows={3}
            value={mode === "image" ? draft.prompt : draft.videoPrompt}
            placeholder={
              mode === "image"
                ? "Describe the change…"
                : "Describe a calm camera movement…"
            }
            onChange={(e) =>
              update(
                mode === "image"
                  ? { prompt: e.target.value }
                  : { videoPrompt: e.target.value },
              )
            }
          />
        </label>
        {mode === "image" ? (
          <>
            <div className="creative-idea-row">
              <div
                className="creative-chips"
                aria-label="Image prompt starters"
              >
                {Object.entries(imagePrompts).map(([label, value]) => (
                  <button
                    type="button"
                    key={label}
                    aria-pressed={draft.prompt.trim() === value}
                    onClick={() =>
                      update({ prompt: withPromptStarter(draft.prompt, value) })
                    }
                  >
                    {label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="creative-area-toggle"
                disabled={!previewSource}
                aria-expanded={guiding}
                aria-pressed={!!draft.area}
                aria-label={
                  guiding
                    ? "Done guiding the area"
                    : draft.area
                      ? "Adjust the area to change"
                      : "Change only part of the image"
                }
                title={
                  previewSource
                    ? guiding
                      ? "Done"
                      : draft.area
                        ? "Adjust area"
                        : "Change only part of the image"
                    : "Reimport this image to enable a lightweight area preview."
                }
                onClick={() => setGuiding(!guiding)}
              >
                {guiding ? "Done" : <Scan size={15} />}
              </button>
            </div>
            {guiding && previewSource && (
              <CreativeAreaGuide
                shape={shape as TLImageShape}
                src={previewSource}
                area={draft.area}
                onChange={(area) => update({ area })}
              />
            )}
            {!guiding && draft.area && (
              <small>
                Only the guided area changes.{" "}
                <button
                  type="button"
                  className="creative-text-button"
                  onClick={() => update({ area: null })}
                >
                  Clear
                </button>
              </small>
            )}
            {!!choices.length && (
              // Shown only when the board has other images; nothing to explain otherwise.
              <section className="creative-references" aria-label="References">
                <small>
                  References
                  {selectedRefs.length > 0 && (
                    <span> · {selectedRefs.length} of 3</span>
                  )}
                </small>
                <div className="creative-reference-grid">
                  {choices.map((choice) => {
                    const selected = selectedRefs.includes(choice.id);
                    return (
                      <button
                        type="button"
                        key={choice.id}
                        aria-pressed={selected}
                        aria-label={choice.name}
                        title={choice.name}
                        disabled={!selected && selectedRefs.length >= 3}
                        onClick={() =>
                          update({
                            references: selected
                              ? selectedRefs.filter((id) => id !== choice.id)
                              : [...selectedRefs, choice.id],
                          })
                        }
                      >
                        {choice.thumb ? (
                          <img src={choice.thumb} alt="" loading="lazy" />
                        ) : (
                          <span>{choice.name}</span>
                        )}
                        {selected && <Check size={13} aria-hidden="true" />}
                      </button>
                    );
                  })}
                </div>
              </section>
            )}
          </>
        ) : (
          <details className="creative-advanced" key={`${key}:${mode}`}>
            <summary>
              Options
              <span>
                {`${draft.duration}s · ${draft.resolution}${draft.audio ? " · Audio" : ""}`}
              </span>
            </summary>
            <div className="creative-advanced-body">
              <div
                className="creative-chips"
                aria-label="Motion prompt starters"
              >
                {Object.entries(motionPrompts).map(([label, value]) => (
                  <button
                    type="button"
                    key={label}
                    onClick={() => update({ videoPrompt: value })}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <label className="creative-duration">
                Length
                <select
                  aria-label="Video length"
                  value={draft.duration}
                  onChange={(e) =>
                    update({
                      duration: e.target.value as CreativeDraft["duration"],
                    })
                  }
                >
                  <option value="5">5 seconds</option>
                  <option value="10">10 seconds</option>
                </select>
              </label>
              <div className="creative-options">
                <label>
                  Resolution
                  <select
                    value={draft.resolution}
                    onChange={(e) =>
                      update({
                        resolution: e.target
                          .value as CreativeDraft["resolution"],
                      })
                    }
                  >
                    <option>720p</option>
                    <option>480p</option>
                  </select>
                </label>
                <label className="creative-check">
                  <input
                    type="checkbox"
                    checked={draft.audio}
                    onChange={(e) => update({ audio: e.target.checked })}
                  />
                  Generate audio
                </label>
                <label>
                  End image
                  <select
                    value={
                      availableIds.has(draft.endImage as TLShapeId)
                        ? draft.endImage
                        : ""
                    }
                    onChange={(e) => update({ endImage: e.target.value })}
                  >
                    <option value="">None</option>
                    {choices.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </label>
                <small>Concept animation, not a measured 3D walkthrough.</small>
              </div>
            </div>
          </details>
        )}
        {staleInputs && (
          <p role="status" className="creative-warning">
            An input is missing. Choose another image to continue.
          </p>
        )}
        <footer className="creative-submit">
          {mode === "image" && generation.status.configured ? (
            <>
              <ImageGenerationActions
                paused={!IMAGE_GENERATION_ENABLED || generation.status.paused}
                canGenerate={generation.status.canGenerate}
                generating={generating}
                disabled={staleInputs || !draft.prompt.trim()}
                hasResult={imageJob?.state === "ready" && !imagePlaced}
                submit={() => void generation.submit(shape, draft)}
              >
                <div
                  className="creative-size"
                  role="group"
                  aria-label="Image size"
                >
                  {(["1K", "2K", "4K"] as const).map((size) => (
                    <button
                      type="button"
                      key={size}
                      aria-pressed={draft.size === size}
                      onClick={() => update({ size })}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </ImageGenerationActions>
              {generation.error && (
                <small role="status">{generation.error}</small>
              )}
            </>
          ) : mode === "video" && video.status.configured ? (
            <VideoGenerationControls
              video={video}
              shape={shape}
              draft={draft}
            />
          ) : (
            <>
              <div>
                <small id="creative-connection-note">
                  AI generation is paused. Your existing work remains available.
                </small>
              </div>
            </>
          )}
        </footer>
      </div>
    </section>
  );
}
